// The location page's sections. Presentational: each takes the one form's
// `control` and owns no state or save of its own.
//
// Deliberately absent: the stream id and the bound service account. The
// service fetches its streams from the server by subject, so nobody ever
// reads or types either; binding happens only by downloading the posting
// service's configuration (service-config-card.tsx). Support finds both in
// the access UI and the database. What the service is DOING — status,
// version, unreadable lines — is on the Service tab (service-status-card.tsx).

import { FormSection, Input, Switch } from "@pej/kore/components";
import type { Control } from "react-hook-form";
import { Field, useWatch } from "@/lib/forms";
import type { ConfigureValues } from "./configure-schema";
import { POSTING_PATH_DESCRIPTION, POSTING_PATH_LABEL, POSTING_PATH_PLACEHOLDER } from "./posting-path";

type ConfigureControl = Control<ConfigureValues>;

export function LocationSection({ control }: { control: ConfigureControl }) {
    return (
        <FormSection title="Location" className="p-5">
            <Field
                name="name"
                control={control}
                label="Name"
                layout="split"
                required
                render={({ field }) => <Input {...field} className="max-w-md" />}
            />
        </FormSection>
    );
}

export function PostingFileSection({ control }: { control: ConfigureControl }) {
    const truncationEnabled = useWatch({ control, name: "truncationEnabled" });
    return (
        <FormSection title="Posting file" className="p-5">
            <Field
                name="filePath"
                control={control}
                label={POSTING_PATH_LABEL}
                layout="split"
                optional
                description={POSTING_PATH_DESCRIPTION}
                render={({ field }) => <Input {...field} className="max-w-md" placeholder={POSTING_PATH_PLACEHOLDER} />}
            />
            <Field
                name="truncationEnabled"
                control={control}
                label="Truncate the posting file"
                layout="split"
                description="The posting service deletes and restarts the posting file once it exceeds the row count below."
                render={({ field: { value, onChange, ...rest } }) => (
                    <Switch checked={value} onCheckedChange={onChange} {...rest} />
                )}
            />
            <Field
                name="truncationMaxRows"
                control={control}
                label="Truncation row limit"
                layout="split"
                required={truncationEnabled}
                description="The posting service deletes the posting file above this row count. Minimum 100."
                render={({ field: { value, onChange, ...rest } }) => (
                    <Input
                        {...rest}
                        type="text"
                        inputMode="numeric"
                        className="max-w-32"
                        disabled={!truncationEnabled}
                        value={value}
                        onChange={(e) => {
                            const digits = e.target.value.replace(/[^0-9]/g, "");
                            onChange(digits === "" ? "" : Number(digits));
                        }}
                    />
                )}
            />
        </FormSection>
    );
}
